import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Notification } from 'iconsax-react';
import toast from 'react-hot-toast';
import { PushPrePermissionModal } from './PushPrePermissionModal';
import { PushState, getPushState, isPushPromotionTarget } from '../../lib/webPush';

/**
 * 헤더 상시 알림 벨 — 자동 노출(PushPermissionGate)과 별개로 유저가 언제든
 * 스스로 찾아갈 수 있는 진입점. 미구독이면 빨간 점 배지가 계속 떠 있고,
 * 클릭하면 프리퍼미션 모달이 열린다. 구독 중이면 상태 확인 토스트만.
 *
 * 미지원 브라우저에서는 아예 렌더하지 않는다.
 * 차단(denied) 상태는 점 없이 벨만 — 클릭하면 모달이 복구 안내로 이어진다.
 */
export const PushBellButton: React.FC<{ variant?: 'desktop' | 'mobile' }> = ({
    variant = 'desktop',
}) => {
    const [state, setState] = useState<PushState | 'loading'>('loading');
    const [open, setOpen] = useState(false);

    const refresh = useCallback(async () => {
        setState(await getPushState());
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // 유도는 모바일 전용 — 데스크톱에서는 벨 자체를 렌더하지 않는다 (설정 토글은 유지)
    if (!isPushPromotionTarget()) return null;
    if (state === 'loading' || state === 'unsupported') return null;

    const needsAttention =
        state === 'default' || state === 'granted-only' || state === 'needs-install';

    const handleClick = () => {
        if (state === 'subscribed') {
            toast.success('실시간 알림이 켜져 있어요 · 설정 > 알림에서 관리할 수 있어요');
            return;
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        refresh();
    };

    return (
        <>
            <motion.button
                onClick={handleClick}
                aria-label="실시간 알림 설정"
                title={needsAttention ? '실시간 방문 알림 켜기' : '실시간 알림'}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`relative flex items-center justify-center rounded-xl border border-[#262626] bg-[#181818] hover:border-[#363636] transition-all duration-300 ${
                    variant === 'mobile' ? 'h-10 w-10 lg:hidden' : 'h-10 w-10'
                }`}
            >
                <Notification
                    size={18}
                    variant={state === 'subscribed' ? 'Bold' : 'Linear'}
                    className={state === 'subscribed' ? 'text-[#3182f6]' : 'text-[#A8A8A8]'}
                    style={{ color: state === 'subscribed' ? '#3182f6' : '#A8A8A8' }}
                />
                {needsAttention && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FF3040] opacity-60" />
                        <span className="relative inline-flex h-3 w-3 rounded-full bg-[#FF3040] ring-2 ring-black" />
                    </span>
                )}
            </motion.button>

            <PushPrePermissionModal isOpen={open} onClose={handleClose} source="bell" />
        </>
    );
};

export default PushBellButton;
